import { faKey } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  CardHeader,
  DialogContent,
  Grid,
  Link,
} from '@mui/material';
import AuthenticatorContext from 'components/Authenticator/AuthenticatorContext';
import FormTextField from 'components/FormFields/FormTextField';
import {
  useCallback,
  useContext,
} from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Title } from 'styles/styles';
import { useLocation } from 'wouter';

import AuthForm from './AuthForm';

interface RecoveryOtpFormData {
  code: string;
}

export default function RecoveryOtp() {
  const formControl = useForm<RecoveryOtpFormData>({ mode: 'onTouched' });
  const [, setLocation] = useLocation();
  const {
    handleSubmit,
  } = formControl;
  const { loginRecoveryOtp, performingLogin, error } = useContext(AuthenticatorContext);

  const onSubmit: SubmitHandler<RecoveryOtpFormData> = useCallback(async ({ code }) => {
    let parsedCode = code;
    if (code.length === 10) {
      parsedCode = `${code.slice(0, 5)}-${code.slice(5, 10)}`;
    }
    await loginRecoveryOtp(parsedCode);
  }, [loginRecoveryOtp]);

  const handleTo2fa = useCallback(() => {
    setLocation('/2fa');
  }, [setLocation]);

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <FormProvider {...formControl}>
        <AuthForm>
          <DialogContent>
            <Grid container spacing={2} direction="column">
              <Grid item display="flex" justifyContent="center">
                <FontAwesomeIcon icon={faKey} size="6x" />
              </Grid>
              <Grid item display="flex" justifyContent="center">
                <CardHeader
                  title={(
                    <Title>
                      <FormattedMessage id="mfa_login_recovery_title" />
                    </Title>
                  )}
                  subheader={(
                    <FormattedMessage id="mfa_login_recovery_subheader" />
                  )}
                />
              </Grid>
              <Grid item display="flex" justifyContent="center" flexDirection="column">
                <Grid container direction="column" paddingLeft={2}>
                  <Grid item justifyContent="center">
                    <FormTextField
                      name="code"
                      type="text"
                      innerFieldLabel="mfa_recovery_code_placeholder"
                      required
                      labelId="mfa_recovery_code_label"
                      size="medium"
                      rules={{
                        required: 'mfa_recovery_code_required',
                      }}
                    />
                  </Grid>
                  <Grid item>
                    {error && (
                      <Alert severity="error">
                        <FormattedMessage id="mfa_recovery_code_error" />
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item display="flex" justifyContent="center" padding={2}>
                <LoadingButton loading={performingLogin} type="submit" variant="contained" color="secondary" size="large" sx={{ width: '80%' }}>
                  <FormattedMessage id="mfa_verify" />
                </LoadingButton>
              </Grid>
              <Grid item display="flex" justifyContent="center" padding={2}>
                <Button
                  type="button"
                  component={Link}
                  onClick={handleTo2fa}
                  sx={{ textTransform: 'none' }}
                >
                  <FormattedMessage id="mfa_login_to_2fa" />
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
        </AuthForm>
      </FormProvider>
    </form>
  );
}
